import type { MapStateToPropsFactory } from "react-redux";
import type { MarketSelection } from "@ppb/tbd-components-flexible-betting-opportunities/components/UpsellSuggestions/model/UpsellSuggestions.graphql";
import type { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import type { BetslipSportsbookReport } from "@ppb/tbd-store/state/betslip/Betslip.types";
import { getSportsbookReport } from "../../store/state/betslip-module/betslip-module-selectors";
import {
  type BetslipAccordionHeaderClick,
  type BetslipSportsbookReAddSelectionsClickAction,
  type BetslipSportsbookReceiptBetIdCopyAction,
  type BetslipSportsbookReceiptRegulatorBetIdCopyAction,
  UI__BETSLIP_ACCORDION_HEADER_CLICK,
  UI__BETSLIP_SBK_RE_ADD_SELECTIONS_CLICK,
  UI__BETSLIP_SBK_RECEIPT_BET_ID_COPY,
  UI__BETSLIP_SBK_RECEIPT_REGULATOR_BET_ID_COPY,
} from "@ppb/tbd-store/actions/betslip";
import { buildPotentialReturns, buildTotalOriginalReturns } from "../betslip-formatters";
import { i18n } from "../../helpers/i18n";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import {
  createSelectionsBuilder,
  createMultiplesBuilder,
  createSinglesBuilder,
  createCastsBuilder,
  createBetBuildersBuilder,
  createMultiBetBuilder,
  createMultiBetBuilderGroups,
  createBoostedMultiplesBuilder,
  createOneLineBetsBuilder,
} from "./sportsbook-receipt-mapper";
import type {
  SportsbookReceiptPanelCallbacks,
  SportsbookReceiptPanelProps,
  SportsbookReceiptReUseSelectionsClick,
} from "./snowflakes/SportsbookReceiptPanel/SportsbookReceiptPanel.types";
import type {
  BetslipLocalization,
  ReceiptTopContentPlacementCapability,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
  SportsbookBetslipSlots,
  UpsellReceiptSuggestionsCapability,
} from "../../contract";

export type ContainerProps = {
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  slots: SportsbookBetslipSlots;
  capabilities: SportsbookBetslipCapabilities;
  receiptTopContentPlacement: ReceiptTopContentPlacementCapability;
  preferences: SportsbookBetslipPreferences;
  upsellReceiptSuggestions: UpsellReceiptSuggestionsCapability;
};

type CardProps = {
  selections: SportsbookReceiptPanelProps["selections"];
  multiples: SportsbookReceiptPanelProps["multiples"];
  boostedMultiples: SportsbookReceiptPanelProps["boostedMultiples"];
  singles: SportsbookReceiptPanelProps["singles"];
  oneLineBets: SportsbookReceiptPanelProps["oneLineBets"];
  casts: SportsbookReceiptPanelProps["casts"];
  betBuilders: SportsbookReceiptPanelProps["betBuilders"];
  multiBetBuilder?: SportsbookReceiptPanelProps["multiBetBuilder"];
  multiBetBuilderGroups: SportsbookReceiptPanelProps["multiBetBuilderGroups"];
  i18n: SportsbookReceiptPanelProps["i18n"];
  totalOriginalReturns?: SportsbookReceiptPanelProps["totalOriginalReturns"];
  totalStake: SportsbookReceiptPanelProps["totalStake"];
  potentialReturns: SportsbookReceiptPanelProps["potentialReturns"];
  isOddsBoosted: SportsbookReceiptPanelProps["isOddsBoosted"];
  hasBoostSignposting: SportsbookReceiptPanelProps["hasBoostSignposting"];
  hasShownReceiptIds: SportsbookReceiptPanelProps["hasShownReceiptIds"];
  isTrapIconEnabled?: boolean;
  marketSelections: MarketSelection[];
};

export type StateProps = CardProps | Record<string, never>;

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const buildSelections = createSelectionsBuilder();
  const buildMultiples = createMultiplesBuilder();
  const buildBoostedMultiples = createBoostedMultiplesBuilder();
  const buildSingles = createSinglesBuilder();
  const buildOneLineBets = createOneLineBetsBuilder();
  const buildCasts = createCastsBuilder();
  const buildBetBuilders = createBetBuildersBuilder();
  const buildMultiBetBuilder = createMultiBetBuilder();
  const buildMultiBetBuilderGroups = createMultiBetBuilderGroups();

  const i18nLabels = {
    multiplesTitleLabel: i18n({ key: "I18N.BETSLIP.MULTIPLES" }),
    boostedMultiplesTitleLabel: i18n({ key: "I18N.BETSLIP.GROUPS.PRICE_BOOST" }),
    selectionsLabel: i18n({ key: "I18N.BETSLIP.SELECTIONS" }),
    oddsLabel: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stakeLabel: i18n({ key: "I18N.BETSLIP.STAKE" }),
    returnsLabel: i18n({ key: "I18N.BETSLIP.RETURNS" }),
    totalStakeLabel: i18n({ key: "I18N.BETSLIP.TOTAL_STAKE" }),
    totalReturnsLabel: i18n({ key: "I18N.BETSLIP.TOTAL_RETURNS" }),
    singlesTitleLabel: i18n({ key: "I18N.BETSLIP.SINGLES" }),
    castsTitleLabel: i18n({ key: "I18N.BETSLIP.CASTS" }),
    multiBetBuilderTitleLabel: i18n({ key: "I18N.BETSLIP.BET_BUILDER_MULTIS" }),
    betBuilderTitleLabel: i18n({ key: "I18N.BETSLIP.BET_BUILDER" }),
    receiptStatusLabel: i18n({ key: "I18N.BETSLIP.RECEIPT_TITLE" }),
    eachWayLabel: i18n({ key: "I18N.BETSLIP.EACHWAY" }),
    accaInsuranceLabel: i18n({ key: "I18N.BETSLIP.ACCA_INSURANCE_APPLIED" }),
    linesLabel: i18n({ key: "I18N.BETSLIP.LINES" }),
    reUseSelectionsLabel: i18n({ key: "I18N.BETSLIP.RE_USE_SELECTIONS" }),
    guaranteedPriceLabel: i18n({ key: "I18N.LABELS.BEST_ODDS_GUARANTEED_REDUCED" }),
    betReceiptIdLabel: i18n({ key: "I18N.MYBETS.BETID" }),
    regulatorBetIdLabel: i18n({ key: "I18N.MYBETS.BETID_AUX" }),
    confirmationMessage: i18n({ key: "I18N.BET.PLACED.CONFIRMATION.MESSAGING" }),
    bonusBallSignpostLabel: i18n({ key: "I18N.BETSLIP.BONUS_BALL_SIGNPOST" }),
  };

  return (state: BetslipApplicationState, { localization, capabilities, preferences }: ContainerProps): StateProps => {
    const {
      result: { totalStake, totalPotentialReturns, originalTotalPotentialReturns, isAnyPriceBoostUsed, runners },
    } = getSportsbookReport(state) as BetslipSportsbookReport;
    const builderProps = { localization, capabilities, oddsDisplay: preferences.oddsDisplay };
    const selections = buildSelections(state, builderProps);

    return {
      selections,
      singles: buildSingles(state, builderProps),
      oneLineBets: buildOneLineBets(state, builderProps),
      multiples: buildMultiples(state, builderProps),
      boostedMultiples: buildBoostedMultiples(state, builderProps),
      casts: buildCasts(state, builderProps),
      betBuilders: buildBetBuilders(state, builderProps),
      multiBetBuilder: buildMultiBetBuilder(state, builderProps),
      multiBetBuilderGroups: buildMultiBetBuilderGroups(state, builderProps),
      i18n: {
        ...i18nLabels,
        selectionsLabel: i18n({
          key: "I18N.BETSLIP.SELECTIONS_COUNT",
          interpolationValues: { numberOfSelections: `${selections.length}` },
        }),
      },
      totalOriginalReturns: buildTotalOriginalReturns(
        totalStake,
        totalPotentialReturns,
        originalTotalPotentialReturns,
        !!isAnyPriceBoostUsed,
        localization,
      ),
      totalStake: currencyFormatWithDecimalPlaces({
        ...localization,
        value: totalStake,
        decimalPlaces: 2,
      }),
      potentialReturns: buildPotentialReturns(totalStake, totalPotentialReturns, localization),
      isOddsBoosted: !!isAnyPriceBoostUsed,
      hasBoostSignposting: capabilities.priceBoostSignposting.enabled,
      hasShownReceiptIds: localization.jurisdiction === "ITALY",
      isTrapIconEnabled: capabilities.trapIcon.enabled,
      marketSelections: Object.values(runners).map(({ marketId, selectionId }) => ({ marketId, selectionId })),
    };
  };
};

const dispatchCopyBetIdAction = (): BetslipSportsbookReceiptBetIdCopyAction => ({
  type: UI__BETSLIP_SBK_RECEIPT_BET_ID_COPY,
});

const dispatchCopyRegulatorBetIdAction = (): BetslipSportsbookReceiptRegulatorBetIdCopyAction => ({
  type: UI__BETSLIP_SBK_RECEIPT_REGULATOR_BET_ID_COPY,
});

export type DispatchProps = {
  dispatchAccordionToggle: SportsbookReceiptPanelCallbacks["onTitleClick"];
  dispatchReUseSelections: SportsbookReceiptReUseSelectionsClick;
  dispatchCopyBetIdAction: typeof dispatchCopyBetIdAction;
  dispatchCopyRegulatorBetIdAction: typeof dispatchCopyRegulatorBetIdAction;
};

export const mapDispatchToProps: DispatchProps = {
  dispatchAccordionToggle: (isExpanded: boolean): BetslipAccordionHeaderClick => ({
    type: UI__BETSLIP_ACCORDION_HEADER_CLICK,
    payload: { isExpanded },
  }),
  dispatchReUseSelections: (selections): BetslipSportsbookReAddSelectionsClickAction => ({
    type: UI__BETSLIP_SBK_RE_ADD_SELECTIONS_CLICK,
    payload: {
      numberOfSelections: selections.length,
    },
  }),
  dispatchCopyBetIdAction,
  dispatchCopyRegulatorBetIdAction,
};
